'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { ClpCourseProvider, TrackedCourse } from './context/clp-course-provider.react-component';
import { CLPPurchaseContextProvider } from './context/clp-purchase-context.react-component';
import { DiscountCodeProvider, useDiscountCodeProvider } from './context/discount-code-provider.react-component';
import { ClpContextProvider } from './context/clp-context-provider.react-component';
import { CoursePriceProvider } from '@udemy-v2/react-course-pricing';
import { CourseWithInstructorDetailsProvider } from './context/course-with-instructor-details-provider.react-component';
import { PriceEventTrackingProvider } from '@udemy-v2/react-currency-formatting';
import { GwgAccelerator } from '@udemy-v2/career-accelerators';
import { CourseCareerAcceleratorProvider } from './context/career-accelerator-provider.react-component';
import { CLPVoucherContextProvider } from './context/voucher-context.react-component';
import { AllGwgAcceleratorsContext } from './context/all-gwg-accelerators-context';
import { CommunityInstructorIdsContext } from './context/community-instructor-ids-context';
import { CareerAcceleratorsByCourseIds } from '@udemy-v2/career-accelerators';
import { CourseLandingPage } from './course-landing-page.react-component';
import { CourseSubscriptionPlansProvider, SubscriptionPlanProductType, SubscriptionPlansProvider } from '@udemy-v2/subscription-graphql';
import { CourseSeoPricing } from '@/features/course-landing-page/components/course-seo/course-seo-pricing.react-component';
import { CoursePreviewVideoProvider } from '@/features/course-landing-page/components/course-preview/context';
import { SmartBarStoreProvider } from '@udemy-v2/react-smart-bar';
import { AssetImageProvider } from './context/asset-image-provider.react-component';
import { AssetImage } from './components/course-preview-entry-point/course-preview-entry-point.react-component';
import { ClpSubscriptionContextProvider } from './context/clp-subscription-context.react-component';
import { ClpNoticeContextProvider } from './context/clp-notice-context-provider.react-component';
import { ClpGtmDataLayer } from './components/google-tag-manager/clp-gtm-data-layer.react-component';
import { FbtBundleConfigProvider } from './context/fbt-bundle-config-provider.react-component';

/**
 * Subscription plan product types relevant for CLP GrowWithGoogle offerings
 */
const CLP_GROW_WITH_GOOGLE_SUBSCRIPTION_PLAN_TYPES = [SubscriptionPlanProductType.GrowWithGoogle, SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle];
interface CourseLandingPageWithProvidersProps {
  course: TrackedCourse;
  courseCareerAccelerator?: CareerAcceleratorsByCourseIds;
  allGwgAccelerators?: GwgAccelerator[];
  communityInstructorIds?: string[];
}

/**
 * Wrapper component that passes discountCodeProvider to CoursePriceProvider
 */
function CoursePriceProviderWithDiscount(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const {
    discountCodeProvider
  } = useDiscountCodeProvider();
  let t1;
  if ($[0] !== children || $[1] !== discountCodeProvider) {
    t1 = <CoursePriceProvider discountCodeProvider={discountCodeProvider}>{children}</CoursePriceProvider>;
    $[0] = children;
    $[1] = discountCodeProvider;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Inner component that sets up the provider hierarchy
 */
function CourseLandingPageWithCoursePriceProvider(props) {
  const $ = _c(37);
  const {
    course,
    courseCareerAccelerator,
    allGwgAccelerators,
    communityInstructorIds
  } = props;
  let t0;
  if ($[0] !== course.images) {
    t0 = <AssetImage images={course.images} />;
    $[0] = course.images;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const preloadedAssetImage = t0;
  const marketingURL = typeof window !== "undefined" ? window.location.href : "https://www.udemy.com";
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...CLP_GROW_WITH_GOOGLE_SUBSCRIPTION_PLAN_TYPES.map(productType => ({
      filters: {
        productType
      },
      marketingURL
    })), {
      filters: {
        productType: SubscriptionPlanProductType.Consumersubscription
      },
      marketingURL
    }];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const subscriptionPlanQueryVariables = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      filters: {
        productType: SubscriptionPlanProductType.Consumersubscription
      },
      marketingURL
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const courseSubscriptionPlanQueryVariables = t2;
  const t3 = String(course.id);
  const t4 = course.urlCourseLanding ?? "";
  const t5 = String(course.id);
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ClpGtmDataLayer />;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      context: "landing-page"
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      forceNoticeType: "smart_bar",
      showSmartBar: true
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== course) {
    t9 = <CourseSeoPricing course={course} />;
    $[7] = course;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CourseLandingPage />;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== communityInstructorIds) {
    t11 = <CommunityInstructorIdsContext.Provider value={communityInstructorIds}>{t10}</CommunityInstructorIdsContext.Provider>;
    $[10] = communityInstructorIds;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t11 || $[13] !== t9) {
    t12 = <SmartBarStoreProvider config={t8} data={null}>{t9}{t11}</SmartBarStoreProvider>;
    $[12] = t11;
    $[13] = t9;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== course.instructors || $[16] !== t12) {
    t13 = <CoursePreviewVideoProvider><ClpContextProvider><PriceEventTrackingProvider priceEventTrackingContext={t7}><CourseWithInstructorDetailsProvider instructors={course.instructors}>{t12}</CourseWithInstructorDetailsProvider></PriceEventTrackingProvider></ClpContextProvider></CoursePreviewVideoProvider>;
    $[15] = course.instructors;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== preloadedAssetImage || $[19] !== t13) {
    t14 = <DiscountCodeProvider><CoursePriceProviderWithDiscount>{t6}<CLPPurchaseContextProvider><CLPVoucherContextProvider><AssetImageProvider preloadedAssetImage={preloadedAssetImage}>{t13}</AssetImageProvider></CLPVoucherContextProvider></CLPPurchaseContextProvider></CoursePriceProviderWithDiscount></DiscountCodeProvider>;
    $[18] = preloadedAssetImage;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== courseCareerAccelerator || $[22] !== t14) {
    t15 = <CourseCareerAcceleratorProvider courseCareerAccelerator={courseCareerAccelerator}>{t14}</CourseCareerAcceleratorProvider>;
    $[21] = courseCareerAccelerator;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== allGwgAccelerators || $[25] !== t15) {
    t16 = <AllGwgAcceleratorsContext.Provider value={allGwgAccelerators}>{t15}</AllGwgAcceleratorsContext.Provider>;
    $[24] = allGwgAccelerators;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== course || $[28] !== t16) {
    t17 = <ClpNoticeContextProvider><ClpCourseProvider course={course}>{t16}</ClpCourseProvider></ClpNoticeContextProvider>;
    $[27] = course;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t17 || $[31] !== t5) {
    t18 = <ClpSubscriptionContextProvider courseId={t5}>{t17}</ClpSubscriptionContextProvider>;
    $[30] = t17;
    $[31] = t5;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t18 || $[34] !== t3 || $[35] !== t4) {
    t19 = <FbtBundleConfigProvider><SubscriptionPlansProvider subscriptionPlanQueryVariables={subscriptionPlanQueryVariables}><CourseSubscriptionPlansProvider courseId={t3} courseLandingUrl={t4} subscriptionPlanQueryVariables={courseSubscriptionPlanQueryVariables}>{t18}</CourseSubscriptionPlansProvider></SubscriptionPlansProvider></FbtBundleConfigProvider>;
    $[33] = t18;
    $[34] = t3;
    $[35] = t4;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
}
export function CourseLandingPageWithProviders(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <CourseLandingPageWithCoursePriceProvider {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}